import React, { useState } from 'react';
import { GameSettings, PlayerProfile } from '../../types';
import { BackButton, Button, Input } from '../ui';
import { THEME_MAP, ThemeType } from '../../constants';
import DiceBackground from '../DiceBackground';
import { useTranslation, Language } from '../../i18n';

const LANGUAGE_OPTIONS: Language[] = ['EN', 'TR'];
const LANGUAGE_LABELS: Record<Language, string> = { EN: 'ENGLISH', TR: 'TÜRKÇE' };

interface SettingsProps {
  settings: GameSettings;
  profile: PlayerProfile;
  onUpdateSettings: (updates: Partial<GameSettings>) => void;
  onUpdateProfile: (name: string) => void;
  onBack: () => void;
  // Auth props
  isGuest?: boolean;
  userEmail?: string | null;
  onSignOut?: () => Promise<void>;
  onNavigateAuth?: () => void;
  onDeleteAccount?: () => Promise<void>;
}

export const Settings: React.FC<SettingsProps> = ({
  settings,
  profile,
  onUpdateSettings,
  onUpdateProfile,
  onBack,
  isGuest = true,
  userEmail,
  onSignOut,
  onNavigateAuth,
  onDeleteAccount,
}) => {
  const t = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-start text-white p-4 pt-8 relative select-none bg-[#1a101f] overflow-hidden">
      <DiceBackground count={12} />

      <BackButton onClick={onBack} />

      <h1 className="text-2xl lg:text-5xl font-bold tracking-tighter mb-4 text-theme drop-shadow-lg uppercase text-center relative z-10 mt-4">{t.settings}</h1>

      <div className="w-full max-w-xs lg:max-w-md flex-1 overflow-y-auto pr-2 custom-scrollbar relative z-10">
        <div className="flex flex-col gap-8 pb-4">
          <section className="flex flex-col gap-3">
            <h2 className="text-[10px] lg:text-xs text-gray-500 uppercase tracking-widest text-center">{t.audio}</h2>
            <div className="flex gap-2">
              <Button
                onClick={() => onUpdateSettings({ isMuted: !settings.isMuted })}
                className={`flex-1 ${settings.isMuted ? 'bg-red-900/40 text-red-400' : 'bg-green-900/40 text-green-400'}`}
              >
                {settings.isMuted ? t.soundOff : t.soundOn}
              </Button>
              <Button
                onClick={() => onUpdateSettings({ isMusicMuted: !settings.isMusicMuted })}
                className={`flex-1 ${settings.isMusicMuted ? 'bg-red-900/40 text-red-400' : 'bg-green-900/40 text-green-400'}`}
              >
                {settings.isMusicMuted ? t.musicOff : t.musicOn}
              </Button>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-[10px] lg:text-xs text-gray-500 uppercase tracking-widest text-center">{t.arenaTheme}</h2>
            <div className="grid grid-cols-3 gap-1.5">
              {(Object.keys(THEME_MAP) as ThemeType[]).map(themeKey => (
                <Button
                  key={themeKey}
                  onClick={() => onUpdateSettings({ theme: themeKey })}
                  size="sm"
                  className={`${settings.theme === themeKey ? 'bg-theme text-white border-white' : 'bg-black/40 text-gray-500'}`}
                >
                  <div className="w-2 h-2 border border-white mr-1.5" style={{ backgroundColor: THEME_MAP[themeKey].primary }} />
                  {themeKey}
                </Button>
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-[10px] lg:text-xs text-gray-500 uppercase tracking-widest text-center">{t.language}</h2>
            <div className="flex justify-between gap-2">
              {LANGUAGE_OPTIONS.map(lang => (
                <Button
                  key={lang}
                  onClick={() => onUpdateSettings({ language: lang })}
                  className={`flex-1 ${settings.language === lang ? 'bg-theme text-white' : 'bg-black/40 text-gray-500'}`}
                >
                  {LANGUAGE_LABELS[lang]}
                </Button>
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-[10px] lg:text-xs text-gray-500 uppercase tracking-widest text-center">{t.warriorName}</h2>
            <div className="p-3 lg:p-5 bg-black/40 text-theme pixel-border text-[9px] lg:text-sm font-bold uppercase text-center tracking-widest">
              {profile.name}
            </div>
            <p className="text-[8px] lg:text-[9px] text-gray-400 text-center">
              {isGuest ? t.signUpToChooseName : t.nameCannotBeChanged}
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-[10px] lg:text-xs text-gray-500 uppercase tracking-widest text-center">{t.account}</h2>
            {isGuest ? (
              <div className="flex flex-col gap-2">
                <p className="text-[8px] lg:text-[10px] text-gray-400 text-center">
                  {t.playingAsGuest}
                </p>
                <Button
                  onClick={onNavigateAuth}
                  variant="warning"
                  size="md"
                >
                  {t.createAccount}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="p-3 bg-black/40 pixel-border text-center">
                  <p className="text-[8px] lg:text-[9px] text-gray-500 uppercase tracking-widest mb-1">{t.signedInAs}</p>
                  <p className="text-[9px] lg:text-xs text-theme font-bold uppercase tracking-wider">{userEmail}</p>
                </div>
                <Button
                  onClick={onSignOut}
                  variant="danger"
                  size="sm"
                >
                  {t.signOut}
                </Button>

                {/* Delete Account */}
                {!showDeleteConfirm ? (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-black/40 text-gray-500 hover:text-red-400 mt-2"
                    size="sm"
                  >
                    {t.deleteAccount}
                  </Button>
                ) : (
                  <div className="pixel-card p-3 lg:p-4 border-l-4 border-l-red-500 bg-red-900/20 mt-2 flex flex-col gap-2">
                    <p className="text-[8px] lg:text-[10px] text-red-400 font-bold uppercase tracking-wider text-center">
                      {t.deleteAccountConfirm}
                    </p>
                    <p className="text-[8px] lg:text-[9px] text-gray-500 text-center">
                      {t.deleteAccountWarning}
                    </p>
                    <div className="flex gap-2 mt-1">
                      <Button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="flex-1 bg-black/40 text-gray-400"
                        size="sm"
                        disabled={isDeleting}
                      >
                        {t.cancel}
                      </Button>
                      <Button
                        onClick={async () => {
                          setIsDeleting(true);
                          try {
                            await onDeleteAccount?.();
                          } catch {
                            setIsDeleting(false);
                            setShowDeleteConfirm(false);
                          }
                        }}
                        variant="danger"
                        size="sm"
                        className="flex-1"
                        disabled={isDeleting}
                      >
                        {isDeleting ? t.deletingAccount : t.yesDelete}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

    </div>
  );
};
